import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { db, collection, query, where, onSnapshot } from "../../../../firebaseConfig";
import {
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import ScheduleIcon from "@mui/icons-material/Schedule";
import InvisibleInk from "../../../../GlobalComponents/InvisibleInk";
import { useAtom } from "jotai";
import { themeAtom } from "../../../../-Atoms";

const Invoices = ({ jobData, checkInvoiceStatus }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [theme] = useAtom(themeAtom);
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // useEffect(() => {
  //   checkInvoiceStatus();
  // }, [jobData]);

  useEffect(() => {
    if (!jobData?.jobNumber) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "xero-invoices"),
      where("containsJobNumbers", "array-contains", jobData.jobNumber.toString())
    );
    
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const invoiceData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setInvoices(invoiceData);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching invoices:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [jobData]);

  if (loading) {
    return null;
  }

  if (invoices.length === 0) {
    return null;
  }

  const formatDate = (dateString) => {
    if (!dateString) return "";

    if (dateString.includes("/Date(")) {
      const timestamp = parseInt(
        dateString.match(/\/Date\((\d+)[+-]\d+\)\//)[1]
      );
      return new Date(timestamp).toLocaleDateString("en-AU", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }

    if (dateString.includes("T")) {
      return new Date(dateString).toLocaleDateString("en-AU", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }

    return dateString;
  };

  const getPaymentStatus = (invoice) => {
    if (invoice.Status === "VOIDED") {
      return "VOIDED";
    } else if (invoice.Status === "DRAFT") {
      return "DRAFT";
    } else if (!invoice.SentToContact) {
      return "DRAFT";
    } else if (invoice.AmountDue <= 0 && invoice.Total > 0) {
      return "Paid";
    } else if (invoice.AmountPaid > 0 && invoice.AmountDue > 0) {
      return "Partially Paid";
    } else {
      return "Unpaid";
    }
  };

  const getDueDateStatus = (dueDateString) => {
    if (!dueDateString) return "";

    let dueDate;
    if (dueDateString.includes("/Date(")) {
      const timestamp = parseInt(
        dueDateString.match(/\/Date\((\d+)[+-]\d+\)\//)[1]
      );
      dueDate = new Date(timestamp);
    } else if (dueDateString.includes("T")) {
      dueDate = new Date(dueDateString);
    } else {
      return "";
    }

    const today = new Date();
    const timeDiff = dueDate.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

    if (daysDiff < 0) {
      return `Overdue by ${Math.abs(daysDiff)} days`;
    } else if (daysDiff === 0) {
      return "Due today";
    } else if (daysDiff === 1) {
      return "Due tomorrow";
    } else {
      return `Due in ${daysDiff} days`;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Paid":
        return "#4caf50";
      case "Partially Paid":
        return "#ff9800";
      case "Unpaid":
        return "#f44336";
      case "VOIDED":
        return "#f44336";
      case "DRAFT":
        return "#2196f3";
      default:
        return theme === "dark" ? "#f5f5f5" : "#757575";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Paid":
        return <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} />;
      case "Partially Paid":
        return <ScheduleIcon fontSize="small" sx={{ mr: 0.5 }} />;
      case "Unpaid":
        return <ErrorIcon fontSize="small" sx={{ mr: 0.5 }} />;
      case "VOIDED":
        return <ErrorIcon fontSize="small" sx={{ mr: 0.5 }} />;
      case "DRAFT":
        return <ScheduleIcon fontSize="small" sx={{ mr: 0.5 }} />;
      default:
        return null;
    }
  };


  const getStatusSummary = () => {
    const statusCounts = invoices.reduce((acc, invoice) => {
      const status = getPaymentStatus(invoice);
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    return (
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
        {Object.entries(statusCounts).map(([status, count]) => (
          <Chip
            key={status}
            icon={getStatusIcon(status)}
            label={`${count} ${status}`}
            size="small"
            sx={{
              backgroundColor:
                theme === "dark"
                  ? "rgba(45,45,45,0.7)"
                  : "rgba(250,250,250,0.9)",
              color: getStatusColor(status),
              fontWeight: 600,
              fontSize: "0.7rem",
              height: 24,
              border: `1px solid ${getStatusColor(status)}`,
              "& .MuiChip-icon": {
                color: getStatusColor(status),
                fontSize: "0.9rem",
              },
              "& .MuiChip-label": {
                paddingLeft: 0.5,
                paddingRight: 0.5,
              },
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          />
        ))}
      </Box>
    );
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    },
    hover: { scale: 1.0 },
    tap: { scale: 0.99 },
  };

  // Section header style
  const sectionHeaderStyle = {
    fontSize: "1rem",
    fontWeight: "bold",
    color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
    marginBottom: 0,
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      style={{
        width: "100%",
        borderRadius: "10px",
        overflow: "hidden",
        backgroundColor:
          theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
        boxShadow:
          theme === "dark"
            ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
            : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
        marginBottom: 16,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <Accordion
        expanded={isExpanded}
        onChange={() => setIsExpanded(!isExpanded)}
        disableGutters
        elevation={0}
        sx={{
          width: "100%",
          backgroundColor: "transparent",
          boxShadow: "none",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            paddingTop: 1.5,
            paddingBottom: 1.5,
            paddingLeft: 2,
            paddingRight: 1.5,
            minHeight: 40,
            "& .MuiAccordionSummary-content": { 
              margin: "4px 0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
            },
            "& .MuiAccordionSummary-expandIconWrapper": {
              color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
            },
          }}
        >
          <Typography variant="h6" sx={sectionHeaderStyle}>
            Invoices
          </Typography>
          {!isExpanded && getStatusSummary()}
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0, paddingBottom: 2, paddingLeft: 2, paddingRight: 2 }}>
        
        {invoices.map((invoice) => (
          <motion.div
            key={invoice.id}
            variants={itemVariants}
            whileHover="hover"
            whileTap="tap"
          >
            <Box
              sx={{
                borderRadius: "8px",
                backgroundColor:
                  theme === "dark" ? "rgba(35,35,35,0.4)" : "rgba(235,235,235,0.4)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                padding: 1.5,
                marginBottom: 1,
                overflow: "visible",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 1,
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{ 
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                      color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
                    }}
                  >
                    {invoice.InvoiceNumber}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{
                      color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
                    }}
                  >
                    {invoice.Reference}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <Chip
                    icon={getStatusIcon(getPaymentStatus(invoice))}
                    label={getPaymentStatus(invoice)}
                    size="small"
                    sx={{
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(45,45,45,0.7)"
                          : "rgba(250,250,250,0.9)",
                      color: getStatusColor(getPaymentStatus(invoice)),
                      fontWeight: 600,
                      border: `1px solid ${getStatusColor(
                        getPaymentStatus(invoice)
                      )}`,
                      "& .MuiChip-icon": {
                        color: getStatusColor(getPaymentStatus(invoice)),
                      },
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 3, mt: 2, mb: 1 }}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ 
                      display: "block",
                      color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
                    }}
                  >
                    Date
                  </Typography>
                  <Typography 
                    variant="body2"
                    sx={{
                      color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
                    }}
                  >
                    {formatDate(invoice.Date)}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    sx={{ 
                      display: "block",
                      color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
                    }}
                  >
                    Due Date
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color:
                        invoice.AmountDue > 0 &&
                        new Date(
                          invoice?.DueDate?.replace(
                            /\/Date\((\d+)[+-]\d+\)\//,
                            "$1"
                          )
                        ) < new Date()
                          ? "#f44336"
                          : theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
                      fontStyle: "italic",
                      mt: 0.5,
                    }}
                  >
                    {invoice.AmountDue > 0
                      ? getDueDateStatus(invoice.DueDate)
                      : ""}
                  </Typography>
                </Box>
              </Box>

              <Divider 
                sx={{ 
                  my: 1.5,
                  borderColor: theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)",
                }} 
              />

              <Box sx={{ mt: 1 }}>
                <Box sx={{ cursor: "pointer" }}>
                  <InvisibleInk
                    numberOfParticles={400}
                    visible={isVisible}
                    setVisible={setIsVisible}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 2,
                        p: 1.5,
                        borderRadius: "8px",
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(45,45,45,0.7)"
                            : "rgba(250,250,250,0.9)",
                        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(55,55,55,0.8)"
                              : "rgba(255,255,255,1)",
                        },
                      }}
                    >
                      <Box sx={{ minWidth: 100 }}>
                        <Typography
                          variant="caption"
                          sx={{ 
                            display: "block",
                            color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
                          }}
                        >
                          Total
                        </Typography>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: "bold",
                            color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
                          }}
                        >
                          ${invoice.Total?.toFixed(2) || "0.00"}
                        </Typography>
                      </Box>

                      <Box sx={{ minWidth: 100 }}>
                        <Typography
                          variant="caption"
                          sx={{ 
                            display: "block",
                            color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
                          }}
                        >
                          Amount Due
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: "bold",
                            color: invoice.AmountDue > 0 ? "#f44336" : 
                              theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
                          }}
                        >
                          ${invoice.AmountDue?.toFixed(2) || "0.00"}
                        </Typography>
                      </Box>

                      <Box sx={{ minWidth: 100 }}>
                        <Typography
                          variant="caption"
                          sx={{ 
                            display: "block",
                            color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
                          }}
                        >
                          Amount Paid
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: "bold",
                            color: invoice.AmountPaid > 0 ? "#4caf50" : 
                              theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
                          }}
                        >
                          ${invoice.AmountPaid?.toFixed(2) || "0.00"}
                        </Typography>
                      </Box>
                    </Box>
                  </InvisibleInk>
                </Box>
              </Box>

              {invoice.LineItems && invoice.LineItems.length > 0 && (
                <Accordion
                  disableGutters
                  elevation={0}
                  sx={{
                    "&:before": { display: "none" },
                    borderTop:
                      theme === "dark"
                        ? "1px solid rgba(255,255,255,0.1)"
                        : "1px solid rgba(0,0,0,0.08)",
                    backgroundColor: "transparent",
                    mt: 1,
                  }}
                >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    px: 1.5,
                    minHeight: 48,
                    "& .MuiAccordionSummary-content": { margin: "8px 0" },
                    "& .MuiAccordionSummary-expandIconWrapper": {
                      color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
                    },
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: "bold",
                      color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
                    }}
                  >
                    Line Items ({invoice.LineItems.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      backgroundColor: theme === "dark" ? "rgba(45,45,45,0.7)" : "rgba(250,250,250,0.9)",
                      border:
                        theme === "dark"
                          ? "1px solid rgba(255,255,255,0.1)"
                          : "1px solid rgba(0,0,0,0.08)",
                      borderRadius: "8px",
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell 
                            sx={{ 
                              fontWeight: "bold",
                              color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
                            }}
                          >
                            Description
                          </TableCell>
                          <TableCell 
                            align="right" 
                            sx={{ 
                              fontWeight: "bold",
                              color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
                            }}
                          >
                            Qty
                          </TableCell>
                          <TableCell 
                            align="right" 
                            sx={{ 
                              fontWeight: "bold",
                              color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
                            }}
                          >
                            Unit Price
                          </TableCell>
                          <TableCell 
                            align="right" 
                            sx={{ 
                              fontWeight: "bold",
                              color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
                            }}
                          >
                            Amount
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invoice.LineItems.map((item, index) => (
                          <TableRow key={item.LineItemID || index}>
                            <TableCell
                              sx={{
                                maxWidth: 250,
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
                              }}
                            >
                              {item.Description}
                            </TableCell>
                            <TableCell 
                              align="right"
                              sx={{
                                color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
                              }}
                            >
                              {item.Quantity}
                            </TableCell>
                            <TableCell align="right">
                              <InvisibleInk
                                numberOfParticles={100}
                                visible={isVisible}
                                setVisible={setIsVisible}
                              >
                                <Typography 
                                  variant="body2"
                                  sx={{
                                    color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
                                  }}
                                >
                                  ${item.UnitAmount?.toFixed(2) || "0.00"}
                                </Typography>
                              </InvisibleInk>
                            </TableCell>
                            <TableCell align="right">
                              <InvisibleInk
                                numberOfParticles={100}
                                visible={isVisible}
                                setVisible={setIsVisible}
                              >
                                <Typography 
                                  variant="body2"
                                  sx={{
                                    color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
                                  }}
                                >
                                  ${item.LineAmount?.toFixed(2) || "0.00"}
                                </Typography>
                              </InvisibleInk>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
              )}
            </Box>
          </motion.div>
        ))}
        </AccordionDetails>
      </Accordion>
    </motion.div>
  );
};

export default Invoices;
